import { useEffect, useState } from "react";
import { Search, Filter, Download, Plus, MoreHorizontal, Check, X, Eye } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { Inbox } from "lucide-react";
import { AdminAPI, fileUrl } from "@/lib/api";
import { toast } from "sonner";

// Petakan anggota dari backend (data telepon & NIK sudah di-mask oleh server, PRD 5.1).
function mapMember(m) {
  return {
    uid: m.id,                                  // id numerik untuk ambil detail
    id: m.member_number || `#${m.id}`,
    name: m.name,
    branch: m.branch || "-",
    joined: m.joined,
    saving: 0,
    status: "Aktif",
    nik_masked: m.nik_masked,
    phone_masked: m.phone_masked,
    role: m.role,
  };
}

const statusLabelId = { Aktif: "Aktif" };

const statusStyle = {
  "Aktif": "bg-sage/20 text-forest",
  "Pending": "bg-amber-warm/20 text-amber-700",
  "Nonaktif": "bg-stone-soft/40 text-ink/75",
};

const emptyForm = { name: "", email: "", phone_number: "", nik: "", password: "" };

export default function AdminMembers() {
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("Semua");
  const [adminMembers, setAdminMembers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);   // hasil memberDetail
  const [detailLoading, setDetailLoading] = useState(false);

  const reload = () =>
    AdminAPI.members().then((rows) => setAdminMembers((rows || []).map(mapMember))).catch(() => {}).finally(() => setLoaded(true));

  useEffect(() => { reload(); }, []);

  // Tambah anggota manual.
  const submitAdd = async () => {
    if (!form.name || !form.phone_number || form.nik.length !== 16 || !form.password) {
      return toast.error("Lengkapi Nama, NIK 16 digit, No. HP, dan Kata Sandi.");
    }
    setSaving(true);
    try {
      const res = await AdminAPI.createMember(form);
      toast.success(`Anggota dibuat: ${res.member_number}`);
      setAddOpen(false); setForm(emptyForm); reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menambah anggota");
    } finally { setSaving(false); }
  };

  // Lihat detail anggota (profil + transaksi + simpanan + pesanan).
  const openDetail = async (uid) => {
    setDetailLoading(true); setDetail({});
    try { setDetail(await AdminAPI.memberDetail(uid)); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal memuat detail"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  // Ekspor CSV dari daftar saat ini.
  const exportCsv = () => {
    const header = ["No. Anggota", "Nama", "Cabang", "Bergabung", "NIK", "Telepon"];
    const lines = adminMembers.map((m) => [m.id, m.name, m.branch, m.joined, m.nik_masked || "", m.phone_masked || ""]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "anggota.csv"; a.click();
    toast.success("CSV diunduh");
  };

  const filtered = adminMembers.filter((m) =>
    (statusF === "Semua" || m.status === statusF) &&
    (m.name.toLowerCase().includes(q.toLowerCase()) || m.id.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Manajemen Anggota</h1>
          <p className="text-ink/75 mt-2 text-sm">Database anggota · approval workflow · audit lengkap.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-lg border-stone-soft" onClick={exportCsv} data-testid="members-export"><Download size={15} /> Ekspor</Button>
          <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" onClick={() => setAddOpen(true)} data-testid="members-add"><Plus size={15} /> Tambah Manual</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Anggota" value={adminMembers.length} accent="text-forest" />
        <Stat label="Aktif" value={adminMembers.filter((m) => m.status === "Aktif").length} accent="text-forest" />
        <Stat label="Menunggu Persetujuan" value={adminMembers.filter((m) => m.status === "Pending").length} accent="text-amber-700" pulse />
        <Stat label="Nonaktif" value={adminMembers.filter((m) => m.status === "Nonaktif").length} accent="text-ink/75" />
      </div>

      <Card className="rounded-xl border-stone-soft p-5">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center mb-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/55" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau ID anggota..." className="pl-9 bg-cream border-stone-soft rounded-lg" data-testid="members-search" />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {["Semua", "Aktif", "Pending", "Nonaktif"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusF(s)}
                data-testid={`members-filter-${s.toLowerCase()}`}
                className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${
                  statusF === s ? "bg-forest text-white" : "bg-cream text-ink/75 hover:bg-stone-soft/30"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                <TableHead className="w-10"><Checkbox /></TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Anggota</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Cabang</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Bergabung</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Total Simpanan</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((m) => (
                <TableRow key={m.id} className="border-stone-soft hover:bg-cream/30" data-testid={`member-row-${m.id}`}>
                  <TableCell><Checkbox /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9"><AvatarFallback className="bg-forest/10 text-forest text-xs font-bold">{m.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                      <div>
                        <div className="font-semibold text-sm text-ink">{m.name}</div>
                        <div className="font-mono text-[11px] text-ink/55">{m.id}</div>
                        {m.nik_masked && <div className="font-mono text-[10px] text-ink/40">NIK {m.nik_masked} · {m.phone_masked}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-ink/75">{m.branch}</TableCell>
                  <TableCell className="text-sm text-ink/75">{formatDate(m.joined)}</TableCell>
                  <TableCell className="text-right text-sm font-display font-semibold text-ink">{formatRupiah(m.saving)}</TableCell>
                  <TableCell>
                    <Badge className={`${statusStyle[m.status]} hover:${statusStyle[m.status]} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{m.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-lg hover:bg-cream" data-testid={`member-actions-${m.id}`}><MoreHorizontal size={16} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuLabel>Aksi</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openDetail(m.uid)} data-testid={`member-detail-${m.id}`}><Eye size={14} /> Lihat Detail</DropdownMenuItem>
                        {m.status === "Pending" && <>
                          <DropdownMenuItem onClick={() => toast.success(`${m.name} disetujui`)} className="text-forest"><Check size={14} /> Setujui</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toast.error("Pendaftaran ditolak")} className="text-terracotta"><X size={14} /> Tolak</DropdownMenuItem>
                        </>}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-terracotta">Blokir Anggota</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loaded && filtered.length === 0 && (
            <div className="py-12 text-center">
              <Inbox className="mx-auto text-ink/30" size={28} />
              <p className="text-sm text-ink/55 mt-2">{q || statusF !== "Semua" ? "Tidak ada anggota yang cocok" : "Belum ada anggota terdaftar"}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-5 text-xs text-ink/55">
          <div>Menampilkan {filtered.length} dari {adminMembers.length} anggota</div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="rounded-lg border-stone-soft">Sebelumnya</Button>
            <Button variant="outline" size="sm" className="rounded-lg border-stone-soft bg-forest text-white">1</Button>
            <Button variant="outline" size="sm" className="rounded-lg border-stone-soft">2</Button>
            <Button variant="outline" size="sm" className="rounded-lg border-stone-soft">Selanjutnya</Button>
          </div>
        </div>
      </Card>

      {/* Dialog Tambah Anggota Manual */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Tambah Anggota Manual</DialogTitle>
            <DialogDescription>Buat akun anggota baru. Nomor anggota & kode referral dibuat otomatis.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Lengkap</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" data-testid="add-member-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">NIK (16 digit)</Label>
              <Input value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value.replace(/\D/g, "") })} maxLength={16} className="rounded-lg" data-testid="add-member-nik" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">No. HP</Label>
              <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} className="rounded-lg" data-testid="add-member-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Email (opsional)</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg" data-testid="add-member-email" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Kata Sandi Awal</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-lg" data-testid="add-member-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submitAdd} disabled={saving} data-testid="add-member-submit">
              {saving ? "Menyimpan…" : "Simpan Anggota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Detail Anggota */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailLoading || !detail?.member ? (
            <div className="py-10 text-center text-ink/55">Memuat detail…</div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{detail.member.name}</DialogTitle>
                <DialogDescription className="font-mono text-xs">{detail.member.member_number}</DialogDescription>
              </DialogHeader>
              {/* Profil ringkas */}
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-2">
                <Row2 l="NIK" v={detail.member.nik_masked} />
                <Row2 l="Telepon" v={detail.member.phone_masked} />
                <Row2 l="Email" v={detail.member.email || "-"} />
                <Row2 l="Cabang" v={detail.member.branch || "-"} />
                <Row2 l="Kode Referral" v={detail.member.referral_code} />
                <Row2 l="Bergabung" v={formatDate(detail.member.joined)} />
              </div>
              {/* Dokumen anggota */}
              {(() => {
                const d = detail.member.documents || {};
                const items = [["KTP", d.ktp], ["Pas Foto", d.pasfoto], ["Bukti Domisili", d.domisili]].filter(([, u]) => u);
                if (items.length === 0) return null;
                const isImg = (u) => /\.(jpe?g|png|webp)$/i.test(u);
                return (
                  <div className="mt-4">
                    <div className="font-display font-semibold text-sm text-ink mb-2">Dokumen</div>
                    <div className="grid grid-cols-3 gap-3">
                      {items.map(([label, url]) => (
                        <a key={label} href={fileUrl(url)} target="_blank" rel="noreferrer" className="block rounded-xl border border-stone-soft overflow-hidden hover:border-forest transition" data-testid={`detail-doc-${label}`}>
                          {isImg(url)
                            ? <img src={fileUrl(url)} alt={label} className="w-full h-24 object-cover bg-cream" />
                            : <div className="h-24 grid place-items-center bg-cream text-forest text-xs font-semibold">PDF</div>}
                          <div className="text-[11px] text-ink/75 px-2 py-1.5 text-center">{label}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Dompet & simpanan */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <MiniStat l="Saldo Belanja" v={detail.member.wallet?.balance_buyer} />
                <MiniStat l="Saldo Penjual" v={detail.member.wallet?.balance_seller} />
                <MiniStat l="Total Simpanan" v={detail.member.savings?.total} />
                <MiniStat l="Sukarela" v={detail.member.savings?.sukarela} />
              </div>
              {/* Riwayat transaksi */}
              <div className="mt-5">
                <div className="font-display font-semibold text-sm text-ink mb-2">Riwayat Transaksi ({detail.transactions?.length || 0})</div>
                <div className="rounded-xl border border-stone-soft divide-y divide-stone-soft/60 max-h-56 overflow-y-auto">
                  {(detail.transactions || []).length === 0 ? (
                    <div className="p-4 text-xs text-ink/55 text-center">Belum ada transaksi.</div>
                  ) : detail.transactions.map((t, i) => (
                    <div key={i} className="flex items-center justify-between p-3 text-xs" data-testid={`detail-trx-${i}`}>
                      <div><div className="font-medium text-ink">{t.type}</div><div className="text-ink/55">{t.reference}</div></div>
                      <div className={`font-semibold ${t.direction === "CREDIT" ? "text-forest" : "text-terracotta"}`}>
                        {t.direction === "CREDIT" ? "+ " : "- "}{formatRupiah(t.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Pesanan */}
              <div className="mt-4">
                <div className="font-display font-semibold text-sm text-ink mb-2">Pesanan ({detail.orders?.length || 0})</div>
                <div className="rounded-xl border border-stone-soft divide-y divide-stone-soft/60 max-h-44 overflow-y-auto">
                  {(detail.orders || []).length === 0 ? (
                    <div className="p-4 text-xs text-ink/55 text-center">Belum ada pesanan.</div>
                  ) : detail.orders.map((o, i) => (
                    <div key={i} className="flex items-center justify-between p-3 text-xs">
                      <span className="font-mono text-ink">{o.order_number}</span>
                      <span className="text-ink/60">{o.status}</span>
                      <span className="font-semibold text-ink">{formatRupiah(o.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row2 = ({ l, v }) => (
  <div className="flex justify-between border-b border-stone-soft/40 py-1"><span className="text-ink/55">{l}</span><span className="font-medium text-ink">{v}</span></div>
);
const MiniStat = ({ l, v }) => (
  <div className="rounded-xl bg-cream p-3">
    <div className="text-[10px] uppercase tracking-wider text-ink/55">{l}</div>
    <div className="font-display font-bold text-sm text-ink mt-1">{formatRupiah(v || 0)}</div>
  </div>
);

const Stat = ({ label, value, accent, pulse }) => (
  <Card className="rounded-xl border-stone-soft p-5">
    <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">{label}</div>
    <div className={`font-display font-bold text-3xl mt-2 ${accent} ${pulse ? "flex items-center gap-2" : ""}`}>
      {value}
      {pulse && <span className="w-2.5 h-2.5 rounded-full bg-amber-warm animate-pulse"></span>}
    </div>
  </Card>
);
